import { useEffect, useState } from "react";
import { motion } from "framer-motion";

/**
 * A minimal, theme-aware bar chart used by the Stats page.
 *
 * Props:
 *   data       Array<{ date: string(YYYY-MM-DD), total: number }>
 *              Oldest → newest.
 *   height     Chart height in pixels (default 180).
 *
 * The rightmost bar is highlighted as "today" (brighter accent + label).
 * Bars scale to the max value in the series; a horizontal baseline is
 * drawn so zero-days remain visible.
 */
export default function BarChart({ data = [], height = 180 }) {
  const [hoverIdx, setHoverIdx] = useState(null);
  const [ready, setReady] = useState(false);

  // One-tick delay so the initial framer animation runs on mount even
  // when the same chart is re-mounted quickly.
  useEffect(() => {
    const id = window.requestAnimationFrame(() => setReady(true));
    return () => window.cancelAnimationFrame(id);
  }, []);

  const values = data.map((d) => d.total || 0);
  const max = Math.max(1, ...values);
  const todayIdx = data.length - 1;

  return (
    <div
      className="relative w-full"
      data-testid="stats-bar-chart"
      style={{ height }}
    >
      {/* baseline */}
      <div className="pointer-events-none absolute bottom-6 left-0 right-0 border-t border-white/[0.06]" />

      <div className="absolute inset-x-0 bottom-6 top-0 flex items-end gap-[3px] sm:gap-1">
        {data.map((d, i) => {
          const pct = ready ? Math.max(2, Math.round((d.total / max) * 100)) : 0;
          const isToday = i === todayIdx;
          const isHover = hoverIdx === i;
          return (
            <div
              key={d.date}
              className="group relative flex h-full flex-1 items-end"
              onMouseEnter={() => setHoverIdx(i)}
              onMouseLeave={() => setHoverIdx((v) => (v === i ? null : v))}
              data-testid="bar-column"
              data-date={d.date}
              data-total={d.total}
              data-today={isToday ? "true" : "false"}
            >
              <motion.div
                initial={{ height: 0 }}
                animate={{ height: `${pct}%` }}
                transition={{
                  duration: 0.6,
                  delay: i * 0.02,
                  ease: [0.16, 1, 0.3, 1],
                }}
                className={
                  "w-full rounded-t-sm transition-colors " +
                  (isToday
                    ? "bg-accent"
                    : d.total === 0
                      ? "bg-white/[0.06]"
                      : "bg-accent/50 group-hover:bg-accent/80")
                }
                style={{
                  boxShadow: isToday
                    ? "0 0 12px rgb(var(--accent) / 0.7)"
                    : d.total > 0
                      ? "0 0 6px rgb(var(--accent) / 0.35)"
                      : "none",
                  minHeight: d.total > 0 ? "4px" : "2px",
                }}
              />
              {/* tooltip */}
              {isHover && (
                <div className="pointer-events-none absolute -top-9 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded-md border border-white/10 bg-black/80 px-2 py-1 font-mono-safe text-[10px] text-text-main shadow-lg backdrop-blur">
                  <div className="text-text-muted">{d.date}</div>
                  <div className="font-mono-num text-accent">{d.total}</div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* axis labels — first, mid, today */}
      <div className="absolute inset-x-0 bottom-0 flex items-center justify-between font-mono-safe text-[10px] text-text-muted/70">
        <span data-testid="axis-label-start">
          {data[0]?.date?.slice(5) || ""}
        </span>
        <span data-testid="axis-label-mid">
          {data[Math.floor(data.length / 2)]?.date?.slice(5) || ""}
        </span>
        <span className="text-accent" data-testid="axis-label-today">
          today
        </span>
      </div>
    </div>
  );
}
